/**
 * @fileoverview One student row of the Task Tracker matrix: sticky name cell, per-lesson cells and the
 * Final Assessment pair; a student still being processed renders a loader cell instead.
 */

import { Fragment } from 'react'
import { Avatar, Group, Loader, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { AssessmentCell } from './TableCells'
import { DeliverableCellTd, LessonAssessmentCells } from './StudentRowCells'
import { cellStyle, stickyBodyStyle } from './tableHelpers'
import { aggregateLessonAssessmentStatus, initials, type ActivitiesByLesson, type CellMap } from './tableModel'
import { resolveTrackStatus } from '../utils/aggregateLessonStatus'
import type { TaskTrackLesson, TaskTrackStudentRow } from '../types'

const AVATAR_SIZE = 28

interface StudentRowProps {
  student: TaskTrackStudentRow
  lessons: TaskTrackLesson[]
  activitiesByLesson: ActivitiesByLesson
  cellMap: CellMap
  /** Cells spanned by the processing loader (every lesson sub-column + final pair). */
  totalColumns: number
  currentUserId?: string
  onTeacherAssessmentClick: (lessonId: string, studentId: string) => void
  onDeliverableCellClick: (activityId: string, studentId: string) => void
  onFinalAssessmentClick?: (studentId: string) => void
}

function StudentCell({ student }: { student: TaskTrackStudentRow }) {
  return (
    <td style={stickyBodyStyle}>
      <Group spacing="sm" noWrap>
        <Avatar size={AVATAR_SIZE} radius="xl" color="blue" src={student.avatarUrl || undefined}>
          {initials(student.firstName, student.lastName)}
        </Avatar>
        <Text size="sm" weight={500} lineClamp={1}>{student.fullName}</Text>
      </Group>
    </td>
  )
}

// The backend is still generating the new participant's assessment structure; the matrix polls until it fills.
function ProcessingRow({ student, totalColumns }: { student: TaskTrackStudentRow; totalColumns: number }) {
  const { t } = useTranslation()
  return (
    <tr>
      <StudentCell student={student} />
      <td colSpan={totalColumns} style={cellStyle}>
        <Group spacing="xs" position="center" noWrap>
          <Loader size="xs" />
          <Text size="xs" color="dimmed">{t('task_tracker.processing.row')}</Text>
        </Group>
      </td>
    </tr>
  )
}

export function TaskTrackerStudentRow(props: StudentRowProps) {
  const { student, lessons, activitiesByLesson, cellMap, totalColumns, currentUserId } = props
  const { onTeacherAssessmentClick, onDeliverableCellClick, onFinalAssessmentClick } = props
  const { t } = useTranslation()
  const label = (key: string) => t(`task_tracker.status.${key}`)

  if (student.processing) return <ProcessingRow student={student} totalColumns={totalColumns} />

  const { finalAssessment } = student
  const finalSelf = resolveTrackStatus(
    finalAssessment.selfAssessmentStatus,
    finalAssessment.selfAssessmentCompleted,
  )
  const finalTeacher = resolveTrackStatus(
    finalAssessment.teacherAssessmentStatus,
    finalAssessment.teacherAssessmentCompleted,
  )

  return (
    <tr>
      <StudentCell student={student} />
      {lessons.map(lesson => {
        const status = aggregateLessonAssessmentStatus(lesson, student.activities, activitiesByLesson)
        return (
          <Fragment key={lesson.id}>
            {lesson.hasAssessments && (
              <LessonAssessmentCells
                lesson={lesson}
                studentId={student.id}
                status={status}
                onTeacherAssessmentClick={onTeacherAssessmentClick}
              />
            )}
            {lesson.deliverableActivities.map(activity => (
              <DeliverableCellTd
                key={activity.id}
                activity={activity}
                student={student}
                cellMap={cellMap}
                teacherStatus={status.teacherStatus}
                currentUserId={currentUserId}
                onClick={onDeliverableCellClick}
              />
            ))}
          </Fragment>
        )
      })}
      <td style={cellStyle}>
        <AssessmentCell
          status={finalSelf}
          enabled
          tooltipCompleted={label('final_assessment_completed')}
          tooltipInProgress={label('final_assessment_in_progress')}
          tooltipPending={label('final_assessment_pending')}
        />
      </td>
      <td style={cellStyle}>
        <AssessmentCell
          status={finalTeacher}
          enabled
          tooltipCompleted={label('final_assessment_completed')}
          tooltipInProgress={label('final_assessment_in_progress')}
          tooltipPending={label('final_assessment_pending')}
          onClick={onFinalAssessmentClick ? () => onFinalAssessmentClick(student.id) : undefined}
        />
      </td>
    </tr>
  )
}
